/**
 * TendenciasSection — Multi-series trend chart + MoM KPIs.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import {
  ResponsiveContainer, ComposedChart, Line, Bar, XAxis, YAxis,
  Tooltip, Legend, CartesianGrid,
} from 'recharts';
import { fetchTrendMulti } from '@/services/api';
import { useUserStore } from '@/store/userStore';

const SERIES = [
  { key: 'ops',      label: 'OPS',       color: '#4A9EFF', type: 'line' },
  { key: 'liderazgo',label: 'Liderazgo', color: '#22C55E', type: 'line' },
  { key: 'nm',       label: 'Near Miss', color: '#F97316', type: 'bar'  },
];

function MomCard({ label, curr, prev, pct, color }) {
  const isUp = pct > 0;
  const isDown = pct < 0;
  const Icon = isUp ? TrendingUp : isDown ? TrendingDown : Minus;
  return (
    <div
      className="rounded-xl p-4 flex items-start gap-3"
      style={{ background: 'var(--vz-bg-elevated)', border: '1px solid var(--vz-border-subtle)' }}
      data-testid={`mom-card-${label.toLowerCase().replace(/\s/g, '-')}`}
    >
      <div className="rounded-lg p-2" style={{ background: color + '15' }}>
        <Icon size={16} style={{ color }} />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--vz-text-muted)' }}>{label}</div>
        <div className="text-xl font-bold mt-0.5" style={{ color }}>{curr}</div>
        {pct !== null && pct !== undefined ? (
          <div className="text-[11px] mt-0.5 flex items-center gap-1" style={{ color: isUp ? '#22C55E' : isDown ? '#EF4444' : 'var(--vz-text-muted)' }}>
            {isUp ? '+' : ''}{pct}%
            <span style={{ color: 'var(--vz-text-muted)' }}>vs {prev} mes ant.</span>
          </div>
        ) : (
          <div className="text-[11px] mt-0.5" style={{ color: 'var(--vz-text-muted)' }}>Sin período anterior</div>
        )}
      </div>
    </div>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg px-3 py-2 shadow-xl text-xs" style={{ background: '#1E2435', border: '1px solid rgba(255,255,255,0.1)' }}>
      <p className="font-semibold mb-1.5 text-white">{label}</p>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2 py-0.5">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
          <span style={{ color: 'rgba(255,255,255,0.7)' }}>{p.name}:</span>
          <span className="font-bold text-white">{p.value}</span>
        </div>
      ))}
    </div>
  );
};

export function TendenciasSection({ filters = {} }) {
  const { token } = useUserStore();

  const { data, isLoading } = useQuery({
    queryKey: ['trend-multi', filters.desde, filters.hasta, filters.sede],
    queryFn: () => fetchTrendMulti({
      desde: filters.desde,
      hasta: filters.hasta,
      sede: filters.sede,
    }),
    staleTime: 5 * 60 * 1000,
    enabled: !!token,
  });

  if (isLoading) return (
    <div data-testid="tendencias-loading">
      <Skeleton className="h-60 w-full" />
    </div>
  );

  if (!data?.periods?.length) return null;

  const mom = data.mom || {};

  return (
    <div className="space-y-4" data-testid="tendencias-section">
      <div>
        <h3 className="text-sm font-bold text-white">Evolución combinada</h3>
        <p className="text-[10px] mt-0.5" style={{ color: 'var(--vz-text-muted)' }}>
          OPS · Liderazgo · Near Miss — actividad mensual acumulada
        </p>
      </div>

      {/* MoM cards */}
      {mom.curr_periodo && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="mom-cards">
          <MomCard label="OPS" curr={mom.ops_curr} prev={mom.ops_prev} pct={mom.ops_pct} color="#4A9EFF" />
          <MomCard label="Liderazgo" curr={mom.lid_curr} prev={mom.lid_prev} pct={mom.lid_pct} color="#22C55E" />
          <MomCard label="Near Miss" curr={mom.nm_curr} prev={mom.nm_prev} pct={mom.nm_pct} color="#F97316" />
        </div>
      )}

      {/* Multi-series chart */}
      <div
        className="rounded-xl p-5"
        style={{ background: 'var(--vz-bg-elevated)', border: '1px solid var(--vz-border-subtle)' }}
        data-testid="trend-multi-chart"
      >
        <ResponsiveContainer width="100%" height={280}>
          <ComposedChart data={data.periods} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" vertical={false} />
            <XAxis
              dataKey="periodo"
              tick={{ fontSize: 10, fill: 'rgba(255,255,255,0.4)' }}
              axisLine={false} tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 10, fill: 'rgba(255,255,255,0.4)' }}
              axisLine={false} tickLine={false}
              width={30}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{ fontSize: 10, color: 'rgba(255,255,255,0.6)', paddingTop: 8 }}
              iconType="circle" iconSize={8}
            />
            {/* NM as bar (background) */}
            <Bar dataKey="nm" name="Near Miss" fill="#F97316" opacity={0.35} radius={[2, 2, 0, 0]} />
            {/* Lines for OPS and Liderazgo */}
            <Line dataKey="ops" name="OPS" stroke="#4A9EFF" strokeWidth={2.5}
              dot={{ fill: '#4A9EFF', r: 3 }} activeDot={{ r: 5 }} type="monotone" />
            <Line dataKey="liderazgo" name="Liderazgo" stroke="#22C55E" strokeWidth={2.5}
              dot={{ fill: '#22C55E', r: 3 }} activeDot={{ r: 5 }} type="monotone" />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
